import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, Star, Users, Wallet, CreditCard, Globe, Bitcoin } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

type PlanId = 'starter' | 'pro' | 'agency';
type AddonId = 'farm50' | 'farm200' | 'farm1000';
type ProviderId = 'cryptocloud' | 'cloudpayments' | 'dlocal' | 'paddle';

interface CurrentSubscription {
  planId: PlanId | 'free';
  planName: string;
  renewalDate: string;
  usage: {
    posts: { used: number; limit: number };
    channels: { used: number; limit: number };
    aiCalls: { used: number; limit: number };
  };
}

interface BillingHistoryItem {
  id: string;
  date: string;
  amount: number;
  currency: string;
  tariff: string;
  status: 'paid' | 'pending' | 'failed' | 'refunded';
  invoiceUrl?: string;
}

interface ReferralInfo {
  link: string;
  invitedCount: number;
  earned: number;
}

interface PlanDef {
  id: PlanId;
  name: string;
  price: number;
  popular?: boolean;
  features: string[];
}

interface AddonDef {
  id: AddonId;
  name: string;
  price: number;
  accounts: number;
}

interface ProviderDef {
  id: ProviderId;
  label: string;
  Icon: typeof Bitcoin;
}

const PLANS: PlanDef[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 19,
    features: ['5 каналов', '500 постов/мес', 'AI-генерация', 'Биржа'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 49,
    popular: true,
    features: ['20 каналов', 'Безлимит постов', 'Парсинг', 'Боты', 'AI-генерация', 'Биржа'],
  },
  {
    id: 'agency',
    name: 'Agency',
    price: 129,
    features: ['100 каналов', 'White-label', 'API', 'Команда до 10', 'Всё из Pro'],
  },
];

const ADDONS: AddonDef[] = [
  { id: 'farm50', name: 'Farm 50', price: 39, accounts: 50 },
  { id: 'farm200', name: 'Farm 200', price: 99, accounts: 200 },
  { id: 'farm1000', name: 'Farm 1000', price: 299, accounts: 1000 },
];

const PROVIDERS: ProviderDef[] = [
  { id: 'cryptocloud', label: 'Crypto (USDT) via CryptoCloud', Icon: Bitcoin },
  { id: 'cloudpayments', label: 'Карта/СБП (Россия) via CloudPayments', Icon: CreditCard },
  { id: 'dlocal', label: 'UPI/Pix (EM) via dLocal', Icon: Globe },
  { id: 'paddle', label: 'Card (Global) via Paddle', Icon: Wallet },
];

const FREE_PLAN_DEFAULT: CurrentSubscription = {
  planId: 'free',
  planName: 'Free',
  renewalDate: '—',
  usage: {
    posts: { used: 0, limit: 0 },
    channels: { used: 0, limit: 0 },
    aiCalls: { used: 0, limit: 0 },
  },
};

const REFERRAL_DEFAULT: ReferralInfo = {
  link: '',
  invitedCount: 0,
  earned: 0,
};

function UsageBar({ used, limit }: { used: number; limit: number }): JSX.Element {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const limitLabel = limit < 0 ? '∞' : limit.toLocaleString();
  return (
    <div>
      <div className="flex justify-between text-xs text-brand-muted mb-1">
        <span>{used.toLocaleString()} / {limitLabel}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-brand-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function Billing(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selectedProvider, setSelectedProvider] = useState<ProviderId>('cryptocloud');
  const [pendingPlan, setPendingPlan] = useState<PlanId | AddonId | null>(null);

  const subQ = useQuery<CurrentSubscription>({
    queryKey: ['billing', 'subscription'],
    queryFn: async () => {
      try {
        return (await api.get<CurrentSubscription>('/payments/subscription/current')).data;
      } catch {
        return FREE_PLAN_DEFAULT;
      }
    },
  });

  const historyQ = useQuery<BillingHistoryItem[]>({
    queryKey: ['billing', 'history'],
    queryFn: async () => {
      try {
        return (await api.get<BillingHistoryItem[]>('/payments/history')).data;
      } catch {
        return [];
      }
    },
  });

  const refQ = useQuery<ReferralInfo>({
    queryKey: ['billing', 'referral'],
    queryFn: async () => {
      try {
        return (await api.get<ReferralInfo>('/users/referral')).data;
      } catch {
        return REFERRAL_DEFAULT;
      }
    },
  });

  const chargeMut = useMutation<{ paymentUrl: string }, Error, { planId: PlanId | AddonId; provider: ProviderId }>({
    mutationFn: async ({ planId, provider }) => {
      const returnUrl = `${window.location.origin}/billing?status=success`;
      const { data } = await api.post<{ paymentUrl: string }>('/payments/charge', {
        planId, provider, returnUrl,
      });
      return data;
    },
    onSuccess: (data) => {
      toast.success(t('billing.redirecting'));
      window.location.href = data.paymentUrl;
    },
    onError: (err) => {
      toast.error(err.message || t('billing.chargeFailed'));
      setPendingPlan(null);
    },
  });

  const cancelMut = useMutation({
    mutationFn: async () => (await api.post('/payments/cancel')).data,
    onSuccess: () => {
      toast.success(t('billing.cancelled'));
      void qc.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleUpgrade = (planId: PlanId | AddonId): void => {
    setPendingPlan(planId);
    chargeMut.mutate({ planId, provider: selectedProvider });
  };

  const copyRef = async (link: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('billing.copied'));
    } catch {
      toast.error('Copy failed');
    }
  };

  const showAddons = Boolean(user?.farmEnabled);

  // Resolved values — queries never error out (caught internally), so data is always available after load
  const subData = subQ.data ?? FREE_PLAN_DEFAULT;
  const historyData = historyQ.data ?? [];
  const refData = refQ.data ?? REFERRAL_DEFAULT;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold text-brand-dark">{t('billing.title')}</h1>

      {/* Current plan */}
      <section aria-label="current-plan">
        {subQ.isLoading && <div className="card text-brand-muted">{t('common.loading')}</div>}
        {!subQ.isLoading && (
          <div className="card">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-sm text-brand-muted">{t('billing.currentPlan')}</div>
                <div className="text-2xl font-bold text-brand-dark">{subData.planName}</div>
                {subData.planId !== 'free' && (
                  <div className="text-sm text-brand-muted mt-1">
                    {t('billing.renewsOn')}: {subData.renewalDate}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => cancelMut.mutate()}
                disabled={cancelMut.isPending || subData.planId === 'free'}
              >
                {t('billing.cancelSubscription')}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm font-medium mb-2">{t('billing.usage.posts')}</div>
                <UsageBar used={subData.usage.posts.used} limit={subData.usage.posts.limit} />
              </div>
              <div>
                <div className="text-sm font-medium mb-2">{t('billing.usage.channels')}</div>
                <UsageBar used={subData.usage.channels.used} limit={subData.usage.channels.limit} />
              </div>
              <div>
                <div className="text-sm font-medium mb-2">{t('billing.usage.ai')}</div>
                <UsageBar used={subData.usage.aiCalls.used} limit={subData.usage.aiCalls.limit} />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Payment provider selection */}
      <section aria-label="payment-provider">
        <h2 className="text-lg font-semibold text-brand-dark mb-3">{t('billing.choosePayment')}</h2>
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" role="radiogroup" aria-label="payment-provider">
            {PROVIDERS.map((p) => {
              const Icon = p.Icon;
              const active = selectedProvider === p.id;
              return (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
                    active ? 'border-brand-primary bg-brand-primary/5' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="provider"
                    value={p.id}
                    checked={active}
                    onChange={() => setSelectedProvider(p.id)}
                    className="accent-brand-primary"
                  />
                  <Icon className="w-5 h-5 text-brand-primary" aria-hidden="true" />
                  <span className="text-sm">{p.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans grid */}
      <section aria-label="plans">
        <h2 className="text-lg font-semibold text-brand-dark mb-3">{t('billing.plans')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`card relative ${plan.popular ? 'ring-2 ring-brand-primary' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-primary text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" /> {t('billing.popular')}
                </div>
              )}
              <div className="text-xl font-bold text-brand-dark">{plan.name}</div>
              <div className="text-3xl font-bold text-brand-primary mt-2">
                ${plan.price}
                <span className="text-base text-brand-muted font-normal">/{t('billing.perMonth')}</span>
              </div>
              <ul className="mt-4 space-y-2">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-brand-primary mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="btn-primary w-full mt-5"
                onClick={() => handleUpgrade(plan.id)}
                disabled={chargeMut.isPending && pendingPlan === plan.id}
                aria-label={`upgrade-${plan.id}`}
              >
                {chargeMut.isPending && pendingPlan === plan.id ? t('common.loading') : t('billing.upgrade')}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Farm addons */}
      {showAddons && (
        <section aria-label="addons">
          <h2 className="text-lg font-semibold text-brand-dark mb-3">{t('billing.farmAddons')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {ADDONS.map((addon) => (
              <div key={addon.id} className="card">
                <div className="text-lg font-semibold text-brand-dark">{addon.name}</div>
                <div className="text-sm text-brand-muted">
                  {addon.accounts.toLocaleString()} {t('billing.accounts')}
                </div>
                <div className="text-2xl font-bold text-brand-primary mt-2">
                  ${addon.price}
                  <span className="text-sm text-brand-muted font-normal">/{t('billing.perMonth')}</span>
                </div>
                <button
                  type="button"
                  className="btn-secondary w-full mt-4"
                  onClick={() => handleUpgrade(addon.id)}
                  disabled={chargeMut.isPending && pendingPlan === addon.id}
                  aria-label={`upgrade-${addon.id}`}
                >
                  {t('billing.addAddon')}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Billing history */}
      <section aria-label="history">
        <h2 className="text-lg font-semibold text-brand-dark mb-3">{t('billing.history')}</h2>
        <div className="card overflow-x-auto">
          {historyQ.isLoading && <div className="text-brand-muted">{t('common.loading')}</div>}
          {!historyQ.isLoading && historyData.length === 0 && (
            <div className="text-brand-muted text-sm">{t('billing.noHistory')}</div>
          )}
          {!historyQ.isLoading && historyData.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brand-muted border-b">
                  <th className="py-2">{t('billing.col.date')}</th>
                  <th className="py-2">{t('billing.col.amount')}</th>
                  <th className="py-2">{t('billing.col.tariff')}</th>
                  <th className="py-2">{t('billing.col.status')}</th>
                  <th className="py-2">{t('billing.col.invoice')}</th>
                </tr>
              </thead>
              <tbody>
                {historyData.map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="py-2">{row.date}</td>
                    <td className="py-2">
                      {row.amount} {row.currency}
                    </td>
                    <td className="py-2">{row.tariff}</td>
                    <td className="py-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${
                          row.status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : row.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : row.status === 'refunded'
                            ? 'bg-slate-100 text-slate-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="py-2">
                      {row.invoiceUrl ? (
                        <a
                          href={row.invoiceUrl}
                          className="inline-flex items-center gap-1 text-brand-primary hover:underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Download className="w-4 h-4" /> PDF
                        </a>
                      ) : (
                        <span className="text-brand-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Referral program */}
      <section aria-label="referral">
        <h2 className="text-lg font-semibold text-brand-dark mb-3">{t('billing.referral.title')}</h2>
        <div className="card">
          {refQ.isLoading && <div className="text-brand-muted">{t('common.loading')}</div>}
          {!refQ.isLoading && (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex-1 min-w-[260px]">
                <div className="text-sm text-brand-muted mb-1">{t('billing.referral.yourLink')}</div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={refData.link}
                    placeholder={t('billing.referral.linkUnavailable') ?? '—'}
                    className="input flex-1"
                    aria-label="referral-link"
                  />
                  <button
                    type="button"
                    className="btn-secondary whitespace-nowrap"
                    disabled={!refData.link}
                    onClick={() => void copyRef(refData.link)}
                  >
                    {t('billing.referral.copy')}
                  </button>
                </div>
              </div>
              <div className="flex gap-6">
                <div>
                  <div className="text-sm text-brand-muted flex items-center gap-1">
                    <Users className="w-4 h-4" /> {t('billing.referral.invited')}
                  </div>
                  <div className="text-2xl font-bold text-brand-dark">{refData.invitedCount}</div>
                </div>
                <div>
                  <div className="text-sm text-brand-muted">{t('billing.referral.earned')}</div>
                  <div className="text-2xl font-bold text-brand-primary">${refData.earned}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
